import { useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { MobileNav } from './MobileNav';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import type { CSSProperties } from 'react';

export interface DashboardLayoutContext {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
}

export function DashboardLayout() {
  const { user, signOut } = useAuth();
  const { tier } = useSubscription();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const handleLogout = useCallback(async () => {
    await signOut();
  }, [signOut]);

  // Extract user display info from Supabase User object — safe typeof check
  const rawName = user?.user_metadata?.full_name;
  const userName = (typeof rawName === 'string' ? rawName : undefined) ?? 'User';
  const userEmail = user?.email ?? 'user@example.com';
  const rawAvatar = user?.user_metadata?.avatar_url;
  const userAvatarUrl = typeof rawAvatar === 'string' && rawAvatar.length > 0 ? rawAvatar : undefined;

  /* ---- Styles ---- */

  const rootStyle: CSSProperties = {
    display: 'flex',
    height: '100vh',
    backgroundColor: '#0a0a0f',
    color: '#ffffff',
    overflow: 'hidden',
  };

  const mainColumnStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  };

  const mainContentStyle: CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: 16,
  };

  return (
    <div style={rootStyle}>
      {/* Desktop sidebar - hidden on mobile, keep Tailwind for responsive visibility */}
      <div className="hidden md:flex md:w-64 md:flex-shrink-0">
        <Sidebar currentTier={tier} />
      </div>

      {/* Mobile navigation overlay */}
      <MobileNav isOpen={sidebarOpen} onClose={closeSidebar} currentTier={tier} />

      {/* Main content area */}
      <div style={mainColumnStyle}>
        <Header
          onToggleSidebar={toggleSidebar}
          userName={userName}
          userEmail={userEmail}
          userAvatarUrl={userAvatarUrl}
          onLogout={handleLogout}
        />

        <main style={mainContentStyle}>
          <Outlet context={{ sidebarOpen, toggleSidebar } satisfies DashboardLayoutContext} />
        </main>
      </div>
    </div>
  );
}
